import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Plus, Minus, Trash2, ShoppingBag, Truck, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart, formatEUR } from "../../contexts/CartContext";
import { useLanguage } from "../../i18n/LanguageContext";

export default function CartDrawer() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    items,
    open,
    closeCart,
    updateQty,
    removeItem,
    subtotalCents,
    shippingCents,
    totalCents,
    remainingForFreeCents,
    freeShippingProgress,
    count,
  } = useCart();
  const isFreeShipping = shippingCents === 0 && items.length > 0;

  const goToCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  const goToTienda = () => {
    closeCart();
    if (typeof window !== "undefined") {
      window.location.hash = "#tienda";
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && closeCart()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="cart-overlay"
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
        />
        <DialogPrimitive.Content
          data-testid="cart-drawer"
          className="fixed top-0 right-0 z-[101] h-full w-full max-w-md bg-[#F9F6F0] border-l border-[#E5E0D8] flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E0D8]">
            <div>
              <DialogPrimitive.Title className="font-serif-display text-2xl text-[#1A1A1A] leading-none">
                {t.cart.title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-[#5C5C5C] uppercase tracking-[0.2em] mt-1">
                {t.cart.itemCount(count)}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              data-testid="cart-close"
              className="h-9 w-9 flex items-center justify-center border border-[#E5E0D8] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] transition-colors"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <ShoppingBag className="h-12 w-12 text-[#C6C0B5] mb-4" strokeWidth={1.2} />
                <p className="text-[#5C5C5C] mb-6">{t.cart.empty}</p>
                <button
                  onClick={goToTienda}
                  data-testid="cart-empty-cta"
                  className="bg-[#1A1A1A] hover:bg-[#000] text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
                >
                  {t.cart.emptyCta}
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-[#E5E0D8]">
                {items.map((it) => (
                  <li
                    key={it.id}
                    data-testid={`cart-item-${it.id}`}
                    className="py-5 flex gap-4"
                  >
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-20 h-20 object-cover bg-white border border-[#E5E0D8]"
                    />
                    <div className="flex-1 flex flex-col">
                      <div className="text-[0.6rem] uppercase tracking-[0.2em] text-[#8B1E0F] font-semibold">
                        {it.unit}
                      </div>
                      <div className="font-serif-display text-base text-[#1A1A1A] leading-tight">
                        {it.name}
                      </div>
                      <div className="mt-auto pt-2 flex items-center justify-between">
                        <div className="inline-flex items-center border border-[#E5E0D8] bg-white">
                          <button
                            data-testid={`cart-dec-${it.id}`}
                            onClick={() => updateQty(it.id, it.quantity - 1)}
                            className="h-8 w-8 flex items-center justify-center hover:bg-[#E5E0D8]"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span
                            data-testid={`cart-qty-${it.id}`}
                            className="w-9 text-center font-mono text-sm"
                          >
                            {it.quantity}
                          </span>
                          <button
                            data-testid={`cart-inc-${it.id}`}
                            onClick={() => updateQty(it.id, it.quantity + 1)}
                            className="h-8 w-8 flex items-center justify-center hover:bg-[#E5E0D8]"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="font-mono text-sm font-semibold text-[#1A1A1A]">
                          {formatEUR(it.price_cents * it.quantity)}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`cart-remove-${it.id}`}
                      onClick={() => removeItem(it.id)}
                      className="self-start text-[#5C5C5C] hover:text-[#8B1E0F] p-1"
                      aria-label={t.cart.remove}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-[#E5E0D8] px-6 py-5 bg-[#F2EDE3] space-y-4">
              {/* Free shipping progress */}
              <div data-testid="cart-free-shipping-banner">
                <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em] font-semibold mb-2">
                  {isFreeShipping ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-[#1A8754]" />
                      <span className="text-[#1A8754]">
                        {t.cart.freeShippingUnlocked}
                      </span>
                    </>
                  ) : (
                    <>
                      <Truck className="h-3.5 w-3.5 text-[#8B1E0F]" />
                      <span className="text-[#1A1A1A]">
                        {t.cart.freeShippingAt(formatEUR(remainingForFreeCents))}
                      </span>
                    </>
                  )}
                </div>
                <div className="h-1.5 bg-[#E5E0D8] overflow-hidden">
                  <div
                    data-testid="cart-free-shipping-progress"
                    className={`h-full transition-all duration-500 ${
                      isFreeShipping ? "bg-[#1A8754]" : "bg-[#8B1E0F]"
                    }`}
                    style={{ width: `${freeShippingProgress}%` }}
                  />
                </div>
              </div>

              {/* Cost breakdown */}
              <div className="space-y-1.5 text-sm">
                <div className="flex items-baseline justify-between">
                  <span className="text-[#5C5C5C]">{t.cart.subtotal}</span>
                  <span
                    data-testid="cart-subtotal"
                    className="font-mono text-[#1A1A1A]"
                  >
                    {formatEUR(subtotalCents)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[#5C5C5C]">{t.cart.shipping}</span>
                  <span
                    data-testid="cart-shipping"
                    className={`font-mono ${
                      isFreeShipping
                        ? "text-[#1A8754] font-semibold"
                        : "text-[#1A1A1A]"
                    }`}
                  >
                    {isFreeShipping ? t.cart.shippingFree : formatEUR(shippingCents)}
                  </span>
                </div>
                <div className="pt-2 mt-2 border-t border-[#1A1A1A]/15 flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold">
                    {t.checkout.total}
                  </span>
                  <span
                    data-testid="cart-total"
                    className="font-serif-display text-2xl text-[#1A1A1A]"
                  >
                    {formatEUR(totalCents)}
                  </span>
                </div>
              </div>

              <button
                onClick={goToCheckout}
                data-testid="cart-checkout-cta"
                className="w-full bg-[#8B1E0F] hover:bg-[#6A160A] text-white py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
              >
                {t.cart.checkout}
              </button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
